import { useMemo, useRef, useState } from "react";
import { currencyName } from "../currencies/meta";
import { INDEX_BY_CODE, searchCurrencies, type SearchEntry } from "../currencies/search";
import type { AppLocale } from "../i18n/format";
import type { Coverage } from "../rates/coverage";
import { coverageCopy } from "../rates/coverageCopy";
import { Sheet } from "./Sheet";
import { CheckIcon, SearchIcon } from "./Icons";

/**
 * The currency picker.
 *
 * Built on Sheet rather than as its own overlay, so the focus trap, the scrim,
 * the Escape handling, the scroll lock and drag-to-dismiss are inherited rather
 * than reimplemented — every one of which had a bug worth fixing the first time.
 *
 * OPENING SPEED IS THE FEATURE. This is the only screen that renders the whole
 * currency table at once, each row with an image. Three things keep it instant,
 * and none of them is a virtualiser:
 *
 *   the index is prebuilt      see search.ts — no work per keystroke
 *   flags below the fold are   loading="lazy" plus explicit width/height, so
 *   never fetched              the layout is final before any image arrives and
 *                              nothing reflows when they land
 *   the list is plain DOM      at this size a virtualiser costs more than it
 *                              saves, and it breaks the A-Z rail's ability to
 *                              scroll to an arbitrary letter
 *
 * If the table ever reaches a thousand rows this decision should be re-measured.
 * At the real size it is not close.
 */

/** Rail letters. Latin only — codes are Latin, and the rail indexes codes. */
const RAIL = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly locale: AppLocale;
  /** Codes already on the Convert screen, in the user's order. */
  readonly selected: readonly string[];
  readonly coverageFor: (code: string) => Coverage;
  readonly onAdd: (code: string) => void;
  /** Asked for a currency already on screen: go to it rather than duplicate it. */
  readonly onReveal: (code: string) => void;
}

export function CurrencyPicker({
  open,
  onClose,
  locale,
  selected,
  coverageFor,
  onAdd,
  onReveal,
}: Props) {
  const ar = locale === "ar";
  const [query, setQuery] = useState("");
  const listRef = useRef<HTMLDivElement>(null);
  /** Which rail letter the finger is on, for the bubble. Null when not dragging. */
  const [railLetter, setRailLetter] = useState<string | null>(null);

  const results = useMemo(() => searchCurrencies(query), [query]);
  const byCode = INDEX_BY_CODE;

  /*
   * SELECTED CURRENCIES ARE PINNED AT THE TOP, and only when not searching.
   * During a search the ranking is the whole point, and hoisting six rows above
   * a deliberate query would push the thing the user just typed off screen.
   */
  const pinned = query === "" ? selected : [];
  const pinnedSet = new Set(pinned);
  const rest = results.filter((r) => !pinnedSet.has(r.entry.code));

  const scrollToLetter = (letter: string) => {
    const list = listRef.current;
    if (!list) return;
    const target = list.querySelector<HTMLElement>(`[data-letter="${letter}"]`);
    if (target) {
      // No smooth scrolling: the rail is a scrubber, and easing a scrubber makes
      // it lag behind the finger that is driving it.
      list.scrollTop = target.offsetTop - list.offsetTop;
    }
  };

  /*
   * THE RAIL IS ONE SURFACE, NOT 26 BUTTONS.
   *
   * Twenty-six 12px targets would violate the no-finger-precision rule outright
   * and be unusable in a thumb. Instead the whole strip takes the pointer, and
   * the letter is derived from where the finger IS — so sliding along it scrubs
   * continuously and never misses. Every coordinate is read from the event in
   * the handler body, never inside a state updater.
   */
  const railFromEvent = (event: React.PointerEvent<HTMLDivElement>): string | null => {
    const box = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientY - box.top) / box.height;
    const index = Math.floor(ratio * RAIL.length);
    return RAIL[Math.max(0, Math.min(RAIL.length - 1, index))] ?? null;
  };

  const railProps = {
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      const letter = railFromEvent(event);
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch {
        /* capture is an enhancement; the scrub still works over the rail */
      }
      setRailLetter(letter);
      if (letter) scrollToLetter(letter);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      if (railLetter === null) return;
      const letter = railFromEvent(event);
      if (letter && letter !== railLetter) {
        setRailLetter(letter);
        scrollToLetter(letter);
      }
    },
    onPointerUp: () => setRailLetter(null),
    onPointerCancel: () => setRailLetter(null),
  };

  const choose = (entry: SearchEntry) => {
    const meta = entry.meta;
    if (meta.withdrawn) return; // never selectable; the row offers its replacement
    if (pinnedSet.has(entry.code) || selected.includes(entry.code)) {
      /*
       * Already on the screen. Neither adding a duplicate nor doing nothing:
       * both leave the user tapping again to find out what happened. Close and
       * take them to the row — the question behind the tap is "where is it?".
       */
      onReveal(entry.code);
      onClose();
      return;
    }
    onAdd(entry.code);
    onClose();
  };

  const renderRow = (entry: SearchEntry, isSelected: boolean, letterAnchor?: string) => {
    const meta = entry.meta;
    const name = currencyName(entry.code, locale);
    const coverage = coverageFor(entry.code);
    const note = coverageCopy(coverage, locale);
    const withdrawn = meta.withdrawn;
    const replacement = withdrawn ? currencyName(withdrawn.replacedBy, locale) : null;

    return (
      <div
        key={entry.code}
        className="pick"
        data-letter={letterAnchor}
        data-coverage={coverage.state === "ok" ? undefined : coverage.state}
        data-selected={isSelected ? "true" : undefined}
      >
        <button
          type="button"
          className="pick__hit"
          onClick={() => choose(entry)}
          /* A withdrawn currency is findable and not choosable. Disabled rather
             than hidden: someone searching for it must be TOLD, not left to
             conclude the app is missing their money. */
          disabled={Boolean(withdrawn)}
          aria-current={isSelected ? "true" : undefined}
        >
          {meta.flag ? (
            <img
              className="pick__flag"
              src={`/flags/${meta.flag}.svg`}
              alt=""
              width={32}
              height={24}
              /* Lazy plus fixed dimensions: rows below the fold cost nothing to
                 open, and the row's height is final before any image lands so
                 nothing reflows as they arrive. */
              loading="lazy"
              decoding="async"
            />
          ) : (
            /* A currency whose flag has not been drawn yet keeps its slot, so
               the column never jitters between rows that have one and rows that
               do not. */
            <span className="pick__flag pick__flag--none" aria-hidden="true" />
          )}
          <span className="pick__text">
            <span className="pick__line">
              <span className="pick__code" dir="ltr">
                {entry.code}
              </span>
              <span className="pick__name">{name}</span>
            </span>
            {meta.countryEn ? (
              <span className="pick__country">
                {ar ? meta.countryAr ?? meta.countryEn : meta.countryEn}
              </span>
            ) : null}
            {withdrawn ? (
              <span className="pick__note">
                {ar
                  ? `سُحبت من التداول. حلّ محلّها ${replacement} (${withdrawn.replacedBy}).`
                  : `Withdrawn from circulation. Replaced by the ${replacement} (${withdrawn.replacedBy}).`}
              </span>
            ) : coverage.state === "withheld" ? (
              <span className="pick__note">{note?.text}</span>
            ) : null}
          </span>

          {isSelected ? (
            <span className="pick__check" aria-hidden="true">
              <CheckIcon />
            </span>
          ) : coverage.state === "caveated" ? (
            <span className="pick__mark" aria-hidden="true" title={note?.text ?? undefined}>
              i
            </span>
          ) : null}
        </button>

        {/* The withdrawn row's one action: take the replacement instead. */}
        {withdrawn ? (
          <button
            type="button"
            className="pick__replacement"
            onClick={() => {
              onAdd(withdrawn.replacedBy);
              onClose();
            }}
          >
            {ar ? `عرض ${withdrawn.replacedBy} بدلاً منها` : `Select ${withdrawn.replacedBy} instead`}
          </button>
        ) : null}
      </div>
    );
  };

  /* Letter anchors for the rail: the first row of each starting letter. */
  const seen = new Set<string>();
  const anchorFor = (code: string) => {
    const first = code[0];
    if (!first || seen.has(first)) return undefined;
    seen.add(first);
    return first;
  };

  return (
    <Sheet
      open={open}
      onClose={onClose}
      locale={locale}
      title={ar ? "إضافة عملة" : "Add currency"}
    >
      <div className="picker">
        <div className="picker__search">
          <SearchIcon className="picker__search-icon" />
          <input
            className="picker__input"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={ar ? "ابحث بالاسم أو الدولة أو الرمز" : "Search name, country or code"}
            aria-label={ar ? "ابحث عن عملة" : "Search currencies"}
            autoComplete="off"
            spellCheck={false}
            enterKeyHint="search"
          />
          {query ? (
            <button
              type="button"
              className="picker__clear"
              onClick={() => setQuery("")}
              aria-label={ar ? "مسح البحث" : "Clear search"}
            >
              ×
            </button>
          ) : null}
        </div>

        <div className="picker__body">
          <div className="picker__list" ref={listRef} role="list">
            {pinned.length > 0 ? (
              <>
                <p className="picker__group">{ar ? "على شاشتك" : "On your screen"}</p>
                {/* A map lookup, not a search. The first version ran a full
                    searchCurrencies() per pinned row — six searches to render
                    six rows the caller had already named. */}
                {pinned.map((code) => {
                  const entry = byCode.get(code);
                  return entry ? renderRow(entry, true) : null;
                })}
                <p className="picker__group">{ar ? "كل العملات" : "All currencies"}</p>
              </>
            ) : null}

            {rest.length === 0 ? (
              <EmptyState query={query} locale={locale} />
            ) : (
              rest.map(({ entry }) =>
                renderRow(entry, selected.includes(entry.code), query === "" ? anchorFor(entry.code) : undefined),
              )
            )}
          </div>

          {/*
           * The rail is hidden while searching: it indexes the alphabet, and a
           * filtered list has no alphabet to index. Leaving it would be a
           * control that scrolls to letters no longer on screen.
           */}
          {query === "" ? (
            <div
              className="picker__rail"
              {...railProps}
              role="slider"
              aria-label={ar ? "انتقل إلى حرف" : "Jump to letter"}
              aria-valuetext={railLetter ?? "A"}
              tabIndex={0}
            >
              {RAIL.map((letter) => (
                <span className="picker__rail-letter" key={letter} aria-hidden="true">
                  {letter}
                </span>
              ))}
            </div>
          ) : null}

          {/* The bubble follows the finger and is never a tap target itself. */}
          {railLetter ? (
            <span className="picker__bubble" aria-hidden="true">
              {railLetter}
            </span>
          ) : null}
        </div>
      </div>
    </Sheet>
  );
}

/**
 * Nothing matched.
 *
 * A blank panel makes the user doubt their spelling rather than the query. If
 * the thing they typed looks like a currency code we deliberately exclude —
 * a fund unit, a withdrawn code we have not catalogued — say that instead, so
 * "not here" reads as a decision rather than a gap.
 */
function EmptyState({ query, locale }: { readonly query: string; readonly locale: AppLocale }) {
  const ar = locale === "ar";
  const looksLikeCode = /^[A-Za-z]{3}$/.test(query.trim());
  const upper = query.trim().toUpperCase();

  /* Units nobody converts into, excluded on purpose — worth naming when asked
     for by code, because silence looks like an oversight. */
  const EXCLUDED = new Set([
    "XXX", "XDR", "XBA", "XBB", "XBC", "XBD", "XSU", "XUA",
    "BOV", "CHE", "CHW", "CLF", "COU", "MXV", "USN", "UYI", "UYW",
  ]);

  if (looksLikeCode && EXCLUDED.has(upper)) {
    return (
      <p className="picker__empty">
        {ar
          ? `${upper} وحدة حسابية لا تُنفَق ولا تُصرَف، فلا نُدرجها.`
          : `${upper} is a settlement or fund unit, not money anyone spends, so we leave it out.`}
      </p>
    );
  }

  return (
    <p className="picker__empty">
      {ar ? (
        <>
          لا نتيجة لـ<span dir="auto">«{query}»</span>. جرّب رمز العملة، أو اسم الدولة.
        </>
      ) : (
        <>
          Nothing matches <span dir="auto">“{query}”</span>. Try the currency code, or the country
          name.
        </>
      )}
    </p>
  );
}
